import React from 'react';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

interface DateRangePickerProps {
  startDate: Date | null;
  endDate: Date | null;
  onChange: (start: Date | null, end: Date | null) => void;
  onApply?: () => void;
  onClear?: () => void;
  maxDate?: Date;
  minDate?: Date;
  /** Show Apply/Clear buttons. Default true */
  showActions?: boolean;
  placeholder?: [string, string];
}

// Custom input that looks like our design system
const CustomInput = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement> & { value?: string; placeholder?: string; hasValue?: boolean }
>(({ value, onClick, placeholder, hasValue }, ref) => (
  <button
    ref={ref}
    onClick={onClick}
    type="button"
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      background: 'white',
      border: `1.5px solid ${hasValue ? '#06b6d4' : '#dee2e6'}`,
      borderRadius: 8,
      padding: '6px 12px',
      fontSize: '0.85rem',
      fontWeight: 500,
      color: hasValue ? '#212529' : '#adb5bd',
      cursor: 'pointer',
      minWidth: 130,
      transition: 'border-color .15s',
      whiteSpace: 'nowrap',
    }}
    onMouseEnter={e => ((e.currentTarget as HTMLButtonElement).style.borderColor = '#06b6d4')}
    onMouseLeave={e => ((e.currentTarget as HTMLButtonElement).style.borderColor = hasValue ? '#06b6d4' : '#dee2e6')}
  >
    <i className="bi bi-calendar3" style={{ color: '#06b6d4', fontSize: 13, flexShrink: 0 }}></i>
    {value || placeholder}
  </button>
));
CustomInput.displayName = 'CustomInput';

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  startDate,
  endDate,
  onChange,
  onApply,
  onClear,
  maxDate = new Date(),
  minDate,
  showActions = true,
  placeholder = ['From date', 'To date'],
}) => {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      <ReactDatePicker
        selected={startDate}
        onChange={(date: Date | null) => onChange(date, endDate)}
        selectsStart
        startDate={startDate ?? undefined}
        endDate={endDate ?? undefined}
        maxDate={endDate ?? maxDate}
        minDate={minDate}
        customInput={<CustomInput placeholder={placeholder[0]} hasValue={!!startDate} />}
        dateFormat="dd MMM yyyy"
        popperPlacement="bottom-start"
        showMonthDropdown
        showYearDropdown
        dropdownMode="select"
      />
      <span style={{ color: '#adb5bd', fontSize: '0.8rem', flexShrink: 0 }}>→</span>
      <ReactDatePicker
        selected={endDate}
        onChange={(date: Date | null) => onChange(startDate, date)}
        selectsEnd
        startDate={startDate ?? undefined}
        endDate={endDate ?? undefined}
        minDate={startDate ?? minDate}
        maxDate={maxDate}
        customInput={<CustomInput placeholder={placeholder[1]} hasValue={!!endDate} />}
        dateFormat="dd MMM yyyy"
        popperPlacement="bottom-start"
        showMonthDropdown
        showYearDropdown
        dropdownMode="select"
      />
      {showActions && (
        <>
          {(startDate || endDate) && onClear && (
            <button
              onClick={onClear}
              className="btn btn-sm btn-outline-secondary"
              style={{ fontSize: '0.8rem', padding: '5px 10px' }}
              title="Clear dates"
            >
              <i className="bi bi-x"></i> Clear
            </button>
          )}
          {startDate && endDate && onApply && (
            <button
              onClick={onApply}
              className="btn btn-sm btn-primary"
              style={{ fontSize: '0.8rem', padding: '5px 12px', background: 'linear-gradient(135deg,#06b6d4,#3b82f6)', border: 'none' }}
            >
              <i className="bi bi-check2 me-1"></i>Apply
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default DateRangePicker;
